import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { useSession } from 'src/contexts/SessionContext';
import { PurchaseCoupon, SubscriptionCoupon } from 'src/models/coupon/_types';
import {
  createPurchaseCoupon,
  createStripeCoupon,
  updatePurchaseCoupon,
} from 'src/models/coupon/coupon';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import api from 'src/utils/ajax';
import * as Yup from 'yup';

import { PaymentPlanSelect } from './Selects/PaymentPlanSelect';
import { SellableSelect } from './Selects/SellableSelect';

type CouponType = PurchaseCoupon | SubscriptionCoupon;

interface CouponFormProps {
  coupon?: CouponType;
  type: 'purchase' | 'subscription';
  afterSave?: (values: Partial<CouponType>) => Promise<void>;
  children: (formikProps: FormikProps<Partial<CouponType>>) => React.ReactNode;
}

export const CouponFormFields: React.FC = () => {
  const { session, user } = useSession();
  const { values, setFieldValue } = useFormikContext<Partial<CouponType>>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.forms.coupon',
  });
  const isSubscription = values.type?.toLowerCase() === 'subscription';
  const isPurchase = values.type?.toLowerCase() === 'purchase';
  const subscriptionValues = isSubscription
    ? (values as SubscriptionCoupon)
    : null;

  return (
    <Stack spacing={3}>
      <FormTextField
        name={isSubscription ? 'name' : 'code'}
        label="Coupon Code"
        placeholder={`eg. SUMMER${new Date().getFullYear()}`}
      />
      {isSubscription && (
        <Stack direction="row" spacing={2}>
          <FormTextField
            name="duration"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                {t('duration')}
                <Tooltip title={t('durationTooltip')}>
                  <Icon
                    icon={faQuestionCircle}
                    size="small"
                  />
                </Tooltip>
              </Stack>
            }
            options={[
              { value: 'once', label: t('oneTime') },
              { value: 'repeating', label: t('multiMonth') },
              { value: 'forever', label: t('forever') },
            ]}
          />

          {subscriptionValues?.duration === 'repeating' && (
            <FormTextField
              name="durationInMonths"
              label={t('numberOfMonths')}
              type="number"
            />
          )}
        </Stack>
      )}
      <FormDatePicker
        name={isPurchase ? 'expiresOn' : 'redeemBy'}
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            {isPurchase ? t('expiresOn') : t('redeemBy')}
            <Tooltip title={t('redeemByTooltip')}>
              <Icon
                icon={faQuestionCircle}
                size="small"
              />
            </Tooltip>
          </Stack>
        }
        placeholder={
          isPurchase ? t('expiresPlaceholder') : t('redeemPlaceholder')
        }
      />
      <Stack direction="row" spacing={2}>
        <FormTextField
          name="discountType"
          label={t('discountTypeLabel', { currency: session.defaultCurrency })}
          options={[
            {
              value: 'abs',
              label: t('amountOff', { currency: session.defaultCurrency }),
            },
            { value: 'per', label: t('percentOff') },
          ]}
          onChange={(event) => {
            const newValue = event?.target.value;
            if (newValue === 'abs') {
              setFieldValue('percentOff', null);
            } else {
              setFieldValue('amountOff', null);
            }
          }}
        />

        {isPurchase ? (
          <FormTextField
            name="discount"
            label={
              values.discountType === 'abs'
                ? t('amountOff', { currency: session.defaultCurrency })
                : t('percentOff')
            }
            type="number"
          />
        ) : values.discountType === 'abs' ? (
          <FormTextField
            name="amountOff"
            label={t('amountOff', { currency: session.defaultCurrency })}
            type="number"
          />
        ) : (
          <FormTextField
            name="percentOff"
            label={t('percentOff')}
            type="number"
          />
        )}
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormTextField
          name={isPurchase ? 'maxUses' : 'maxRedemptions'}
          label="How many times can this coupon be used?"
          type="number"
          placeholder={t('maxRedemptionsPlaceholder')}
        />
        <FormTextField
          name="limitPerUser"
          label={t('limitPerUser')}
          type="number"
          placeholder={t('limitPerUserPlaceholder')}
        />
      </Stack>
      <FormTextField
        name="displayText"
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            {t('marketingCopy')}
            <Tooltip title={t('marketingCopyTooltip')}>
              <Icon
                icon={faQuestionCircle}
                size="small"
              />
            </Tooltip>
          </Stack>
        }
        placeholder={t('marketingCopyPlaceholder')}
      />
      <SellableSelect
        label={isSubscription ? t('selectItem') : t('selectPurchase')}
        placeholder={isSubscription ? t('anyItem') : t('anyPurchase')}
        value={(values.purchases || []).map(
          (p) => `${p.purchaseId} ${p.purchaseType}`
        )}
        multiple
        helperText={isSubscription ? t('itemHelperText') : undefined}
        onChange={(newValue) => {
          if (
            isSubscription &&
            Array.isArray(newValue) &&
            newValue.length > 5
          ) {
            toast.error(t('maxItems'));
            return;
          }

          setFieldValue(
            'purchases',
            Array.isArray(newValue)
              ? newValue.map((v) => ({
                  purchaseId: v.value.id,
                  purchaseType: v.value.type,
                }))
              : []
          );
        }}
        type={values.type}
        includeOther={false}
      />
      {isSubscription && (
        <PaymentPlanSelect
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              {t('stripePlans')}
              <Tooltip title={t('stripePlansTooltip')}>
                <Icon
                  icon={faQuestionCircle}
                  size="small"
                />
              </Tooltip>
            </Stack>
          }
          placeholder={t('anyPlan')}
          value={subscriptionValues?.stripePlanIds || []}
          onChange={(newValue) => {
            setFieldValue(
              'stripePlanIds',
              Array.isArray(newValue)
                ? newValue.map((p) => p.id)
                : [newValue.id]
            );
          }}
          userId={user?.id}
          multiple
        />
      )}
      <FormCheckbox
        name="firstPurchaseOnly"
        label={
          (values.sellableIds?.length || 0) > 0
            ? t('firstPurchaseOnlySpecific')
            : t('firstPurchaseOnlyGeneral')
        }
      />
    </Stack>
  );
};

export const CouponForm: React.FC<CouponFormProps> = ({
  coupon,
  type,
  afterSave,
  children,
}) => {
  const defaultValues: Partial<CouponType> = {
    type,
    ...(type === 'subscription' ? { name: coupon?.name || '' } : {}),
    ...(type === 'purchase' ? { code: coupon?.code || '' } : {}),
    discountType: coupon?.discountType || 'abs',
    ...(type === 'purchase'
      ? { discount: 1 }
      : { amountOff: type === 'subscription' ? 1 : 0 }),
    ...(type === 'subscription' ? { duration: 'once' } : {}),
    firstPurchaseOnly: false,
    sellableIds: [],
    displayText: '',
    ...coupon,
  };

  // Convert string dates to Date objects before spreading coupon
  const convertedCoupon = { ...coupon };
  if (
    type === 'purchase' &&
    (coupon as any)?.expiresOn &&
    typeof (coupon as any).expiresOn === 'string'
  ) {
    const dateString = (coupon as any).expiresOn;
    const [year, month, day] = dateString.split('-').map(Number);
    (convertedCoupon as any).expiresOn = new Date(year, month - 1, day);
  }
  if (
    type === 'subscription' &&
    (coupon as any)?.redeemBy &&
    typeof (coupon as any).redeemBy === 'string'
  ) {
    const dateString = (coupon as any).redeemBy;
    const [year, month, day] = dateString.split('-').map(Number);
    (convertedCoupon as any).redeemBy = new Date(year, month - 1, day);
  }

  // Now spread the converted coupon
  Object.assign(defaultValues, convertedCoupon);

  const baseValidationSchema = {
    ...(type === 'subscription' ? { name: Yup.string().required() } : {}),
    ...(type === 'purchase' ? { code: Yup.string().required() } : {}),
    redeemBy: Yup.date().nullable(),
    expiresOn: Yup.date().nullable(),
    displayText: Yup.string().nullable(),
    maxRedemptions: Yup.number().min(0).nullable(),
    maxUses: Yup.number().min(0).nullable(),
    limitPerUser: Yup.number().min(0).nullable(),
    firstPurchaseOnly: Yup.boolean().nullable(),
    sellableIds: Yup.array().of(Yup.string()),
  };

  const purchaseValidationSchema = Yup.object().shape({
    ...baseValidationSchema,
    discountType: Yup.string().oneOf(['abs', 'per']).required(),
    discount: Yup.number()
      .required()
      .min(0)
      .when('discountType', {
        is: 'per',
        then: (schema) => schema.max(100),
      })
      .when('discountType', {
        is: 'abs',
        then: (schema) => schema.required().min(1),
      }),
  });

  const subscriptionValidationSchema = Yup.object().shape({
    ...baseValidationSchema,
    duration: Yup.string().oneOf(['once', 'repeating', 'forever']).required(),
    durationInMonths: Yup.number().when('duration', {
      is: 'repeating',
      then: (schema) => schema.required().min(1),
    }),
    discountType: Yup.string().oneOf(['abs', 'per']).required(),
    amountOff: Yup.number()
      .when('discountType', {
        is: 'abs',
        then: (schema) => schema.required().min(1),
      })
      .nullable(),
    percentOff: Yup.number()
      .when('discountType', {
        is: 'per',
        then: (schema) => schema.required().min(1).max(100),
      })
      .nullable(),
    stripePlanIds: Yup.array().of(Yup.string()),
  });

  const validationSchema =
    type.toLowerCase() === 'purchase'
      ? purchaseValidationSchema
      : subscriptionValidationSchema;

  const mutation = useMutation({
    mutationFn: async (values: Partial<CouponType>) => {
      const { type, ...rest } = values;

      if ('amountOff' in rest && rest.amountOff === null) {
        delete rest.amountOff;
      }

      if ('firstPurchaseOnly' in rest && rest.firstPurchaseOnly === false) {
        delete rest.firstPurchaseOnly;
      }

      if (type?.toLowerCase() === 'purchase') {
        if (coupon?.id) {
          return await updatePurchaseCoupon({
            id: coupon.id,
            ...rest,
          });
        }

        return await createPurchaseCoupon(rest as Partial<PurchaseCoupon>);
      }

      rest.id = rest.name;

      return await createStripeCoupon(rest as Partial<SubscriptionCoupon>);
    },
    onSuccess: afterSave,
    onError: (error: Error) => {
      const isEditing = Boolean(coupon?.id);
      const errorMessage = isEditing
        ? 'Error updating coupon'
        : 'Error creating coupon';
      toast.error(api.formatError(error, errorMessage));
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await mutation.mutateAsync(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {(formikProps) => (
        <Form>{children(formikProps as FormikProps<Partial<CouponType>>)}</Form>
      )}
    </Formik>
  );
};
